import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { DealStatusEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import InvestmentAmountSlider from 'components/InvestmentAmountSlider';

import Button from 'primitives/Button';

import constants from 'utils/constants';
import {
  formatCurrency,
  formatCurrencyToShort,
  formatLocalPrice,
  toTitleCase,
} from 'utils/format-helper';

import { INVESTMENT_INCREMENT, MAX_INVESTMENT, MIN_INVESTMENT } from './index';

const BasketsSelectStep = () => {
  const {
    recommendedDeals,
    selectedDeals,
    handleToggleDeal,
    selectedDealDetails,
    investmentAmount,
    handleInvestmentAmountChange,
    handleProceedToConfirmation,
  } = useOutletContext<any>();

  const selectedDealCount = selectedDealDetails.length;
  const dynamicMinInvestment = selectedDealCount ? 50000 * selectedDealCount : MIN_INVESTMENT;

  return (
    <section className="space-y-6 pt-4">
      <div className="space-y-2">
        <p className="text-sm text-slate-500 uppercase tracking-wide">AngelList India</p>
        <h2 className="text-3xl font-semibold text-slate-900">Featured Investment Opportunities</h2>
        <p className="text-slate-500">
          Select the deals you’d like to include in your investment case and invest in one go.
        </p>
      </div>
      <p className="text-sm text-slate-500">Click on any deal card to include or remove it.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {recommendedDeals.map(deal => {
          const isFunded =
            deal.status !== DealStatusEnumType.Raising &&
            deal.status !== DealStatusEnumType.Pooling;
          const isSelected = selectedDeals.includes(deal.id);
          const industries = deal.companyInformation.industries;
          const CompanyLogo = () => {
            const [imageError, setImageError] = useState(false);
            const company = deal.companyInformation.company;
            const hasImage = company.image && !imageError;

            return (
              <div
                className={classNames(
                  'flex items-center justify-center w-12 h-12 rounded-lg overflow-hidden flex-shrink-0',
                  isFunded
                    ? 'bg-slate-700 border border-slate-600'
                    : 'bg-white border border-slate-200'
                )}
              >
                {hasImage ? (
                  <img
                    src={company.image || constants.ALT_AVATAR_URL}
                    alt={company.name}
                    className="w-full h-full object-contain p-2"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <BuildingOfficeIcon
                    className={classNames(
                      'h-6 w-6',
                      isFunded ? 'text-slate-300' : 'text-slate-500'
                    )}
                  />
                )}
              </div>
            );
          };

          if (isFunded) {
            return (
              <div
                key={deal.id}
                className="rounded-2xl p-5 bg-slate-700 border border-slate-600 w-full relative overflow-hidden min-h-[280px] flex flex-col shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, rgb(51 65 85) 0%, rgb(30 41 59) 100%)',
                }}
              >
                <div className="relative z-10 flex flex-col flex-1 items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <CompanyLogo />
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-lg font-semibold text-white text-center">
                        {deal.companyInformation.company.name}
                      </p>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                        FUNDED
                      </span>
                    </div>
                  </div>
                  {deal.publicData?.description && (
                    <p className="text-sm text-slate-200 leading-relaxed mt-4 text-center">
                      {deal.publicData.description}
                    </p>
                  )}
                </div>
              </div>
            );
          }

          return (
            <Button
              key={deal.id}
              variant="custom"
              className={classNames(
                'rounded-2xl p-5 text-left transition relative w-full',
                isSelected
                  ? 'bg-white shadow-lg ring-2 ring-indigo-200'
                  : 'bg-slate-50 border border-slate-100'
              )}
              onClick={() => handleToggleDeal(deal.id)}
            >
              <div className="flex items-start gap-3">
                <CompanyLogo />
                <div className="flex-1 flex items-start justify-between min-w-0">
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-slate-900">
                      {deal.companyInformation.company.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {industries?.length ? toTitleCase(industries[0]) : 'Sector TBD'}
                    </p>
                  </div>
                  {isSelected ? (
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold flex-shrink-0 ml-2">
                      ✓
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-slate-200 text-slate-600 flex-shrink-0 ml-2">
                      Tap to select
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {deal.publicData?.description && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {deal.publicData.description}
                  </p>
                )}
                {industries && industries.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {industries.map((industry, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-sm"
                      >
                        {toTitleCase(industry)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <dt>Round</dt>
                  <dd className="font-medium text-slate-900">
                    {toTitleCase(deal.dealTerms?.fundingRoundName || '')}
                  </dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Valuation</dt>
                  <dd className="font-medium text-slate-900">
                    {formatCurrencyToShort(Number(deal.dealTerms?.valuationOrCap || 0))}
                  </dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Instrument</dt>
                  <dd className="font-medium text-slate-900">
                    {toTitleCase(deal.dealTerms?.financialInstrument || '')}
                  </dd>
                </div>
              </dl>
              <a
                href={deal.publicData?.dealInviteLink}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 mt-4"
                target="_blank"
                rel="noreferrer"
              >
                View deal →
              </a>
            </Button>
          );
        })}
      </div>
      <div className="pt-6" />
      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 mt-12 space-y-4">
        <div>
          <p className="text-sm text-slate-500">Invest Equally</p>
          <h3 className="text-2xl font-semibold text-slate-900">Across All Selected Companies</h3>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Investment Amount</label>
          <input
            type="number"
            min={dynamicMinInvestment}
            max={MAX_INVESTMENT}
            step={INVESTMENT_INCREMENT}
            value={investmentAmount}
            onChange={event => handleInvestmentAmountChange(Number(event.target.value))}
            className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-lg font-semibold text-slate-900 focus:outline-none focus:border-indigo-500"
          />
          <p className="mt-2 text-sm text-slate-500">
            {selectedDealDetails.length
              ? `${formatCurrency(
                  Math.round(investmentAmount / Math.max(selectedDealDetails.length, 1))
                )} per company`
              : 'Select at least one deal to continue'}
          </p>
        </div>
        <div className="py-8">
          <InvestmentAmountSlider
            value={investmentAmount}
            onChange={handleInvestmentAmountChange}
            min={dynamicMinInvestment}
            max={MAX_INVESTMENT}
            increment={INVESTMENT_INCREMENT}
          />
        </div>
        <Button
          variant="primary"
          className="w-full px-4 py-3 disabled:opacity-50"
          disabled={!selectedDealDetails.length}
          onClick={() => handleProceedToConfirmation()}
        >
          Invest ₹{formatLocalPrice(investmentAmount, 0)}
        </Button>
      </div>
    </section>
  );
};

export default BasketsSelectStep;
